import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import {
  getShapeForEventTracking,
  ShapeState,
  ShapeState2D,
} from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import {
  isPointCloudOutput,
  isVideoOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import { ProjectionType } from '../../../cross-team-shared/misc-utils/projection-utils';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { RootPushAndTrackService } from '../../outputs/annotation/annotation-shared/utils/root-push-and-track.service';
import { getProjectionTypeFromTask } from '../utils/projection-utils';
import { AnnotationCreateDeleteService } from './annotation-create-delete.service';
import { ShapeService } from './shape.service';

@Injectable()
export class PointCloudToVectorShapeService {
  private shapeService = inject(ShapeService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);
  private messagingService = inject(MessagingService);
  private pushAndTrackService = inject(RootPushAndTrackService);

  createProjectedRectangles(
    originShapeId: number,
    eventTrackingValue: EventTrackingValue,
  ) {
    const pointCloudContentConnector = this.getPointCloudContentConnector();
    const projectableOutputs = this.getProjectableOutputs();

    const rectangleStates = projectableOutputs
      .map((output) => {
        const points = pointCloudContentConnector.getProjectedRectanglePoints(
          originShapeId,
          output.name,
        );

        if (isUndefined(points)) {
          return;
        }

        return this.createRectangleState(originShapeId, output.name, points);
      })
      .filter(isDefined);

    const shapesCreated = this.createRectangles(rectangleStates);
    this.trackEvent(shapesCreated, eventTrackingValue);
    this.showMessage(shapesCreated);
  }

  getProjectableOutputs() {
    const { project } = this.taskWorkspaceStore.state();
    const supportedProjectionTypes: ProjectionType[] = [
      'cylinder',
      'deformed_cylindrical',
      'pinhole',
      'brown_conrady',
    ];

    return project.outputs.filter(
      (output) =>
        isVideoOutput(output) &&
        !output.meta.is_orthographic_projection &&
        output.meta.available_tools.includes('Rectangle') &&
        supportedProjectionTypes.includes(
          getProjectionTypeFromTask(
            this.taskWorkspaceStore.state(),
            output.name,
          ),
        ),
    );
  }

  private createRectangleState(
    originShapeId: number,
    videoOutputName: string,
    points: Point2D[],
  ): ShapeState2D {
    const shapeState = this.shapeService.getShapeStore(originShapeId).state();

    return {
      type: 'rectangle',
      index: this.taskWorkspaceStore.claimNextInternalId(),
      outputName: videoOutputName,
      tags: {},
      dynamic_tags: {},
      parentId: shapeState.parentId,
      key_locations: [
        {
          frame_number: this.taskWorkspaceStore.getFirstFusion().frameNumber,
          points,
          visibility: 1,
        },
      ],
    };
  }

  private createRectangles(shapes: ShapeState2D[]) {
    return shapes.map((shape) => {
      assert(
        shape.outputName,
        'creating projected shapes is only allowed in objects',
      );
      const shapeCreated = this.annotationCreateDeleteService.createShape(
        shape,
        shape.outputName,
        true,
        shape.parentId,
      );

      assert(shapeCreated, 'shape creation should be prevented if not allowed');
      return shapeCreated;
    });
  }

  private getPointCloudContentConnector() {
    const pointCloudOutput = this.taskWorkspaceStore
      .state()
      .project.outputs.find(isPointCloudOutput);
    assert(pointCloudOutput, 'Must have point cloud output');

    const fusionManager = this.fusionManagerFactory.getManager(
      pointCloudOutput.id,
    );

    return fusionManager.getPrimaryMinion().getPointCloudContentConnector();
  }

  private showMessage(shapes: ShapeState[]) {
    this.messagingService.success(`${shapes.length} shapes created`);
  }

  private trackEvent(
    shapes: ShapeState[],
    eventTrackingValue: EventTrackingValue,
  ) {
    this.pushAndTrackService.pushAndTrack({
      id: 'create_shape',
      bucket: 'annotation',
      ...eventTrackingValue,
      shapes: shapes.map(getShapeForEventTracking),
    });
  }
}
